"use client"

import { useState, useEffect } from "react"
import { api } from "../../services/api"

export default function DemDisponibilites() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDates, setSelectedDates] = useState(new Set())
  const [missionDates, setMissionDates] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState("")
  const [selectedMissionDate, setSelectedMissionDate] = useState(null)
  const [missionDetails, setMissionDetails] = useState(null)
  const [showMissionModal, setShowMissionModal] = useState(false)
  const [viewMode, setViewMode] = useState("month")
  const [timeSlots, setTimeSlots] = useState({})
  const [showTimeSlots, setShowTimeSlots] = useState(false)
  const [bulkOperation, setBulkOperation] = useState("none")
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiSuggestion, setAiSuggestion] = useState("")
  const [aiLoading, setAiLoading] = useState(false)
  const [filterMode, setFilterMode] = useState("all") // all, available, missions, conflicts
  const [searchQuery, setSearchQuery] = useState("")
  const [showHelp, setShowHelp] = useState(false)

  // Helper: format date in local timezone as YYYY-MM-DD
  const toLocalYmd = (d) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  }

  // Générer le calendrier pour le mois actuel
  const generateCalendar = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)

    const startDay = firstDay.getDay()
    const adjustedStartDay = startDay === 0 ? 6 : startDay - 1

    const days = []

    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(null)
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i))
    }

    return days
  }

  // Générer la semaine actuelle
  const generateWeek = (date) => {
    const startOfWeek = new Date(date)
    const day = startOfWeek.getDay()
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
    startOfWeek.setDate(diff)

    const days = []
    for (let i = 0; i < 7; i++) {
      days.push(new Date(startOfWeek))
      startOfWeek.setDate(startOfWeek.getDate() + 1)
    }
    return days
  }

  const calendarDays = viewMode === "month" ? generateCalendar(currentDate) : generateWeek(currentDate)

  // Filtrage des jours selon le mode
  const filteredDays = calendarDays.filter((date) => {
    if (!date) return false
    if (filterMode === "available") return selectedDates.has(toLocalYmd(date))
    if (filterMode === "missions") return missionDates.has(toLocalYmd(date))
    if (filterMode === "conflicts") {
      const dateStr = toLocalYmd(date)
      return selectedDates.has(dateStr) && missionDates.has(dateStr)
    }
    return true
  })

  const toggleDate = (date) => {
    if (!date) return
    const dateString = toLocalYmd(date)
    const newSelected = new Set(selectedDates)

    if (newSelected.has(dateString)) {
      newSelected.delete(dateString)
    } else {
      newSelected.add(dateString)
    }

    setSelectedDates(newSelected)
  }

  const handleMissionDateClick = async (date) => {
    if (!date) return
    const dateString = toLocalYmd(date)
    if (missionDates.has(dateString)) {
      try {
        const response = await api.getMyMissionByDate(dateString)
        setMissionDetails(response.data)
        setSelectedMissionDate(dateString)
        setShowMissionModal(true)
      } catch (error) {
        console.error("Erreur récupération détails mission:", error)
      }
    }
  }

  const isDateSelected = (date) => {
    if (!date) return false
    return selectedDates.has(toLocalYmd(date))
  }

  const isMissionDate = (date) => {
    if (!date) return false
    return missionDates.has(toLocalYmd(date))
  }

  const isToday = (date) => {
    if (!date) return false
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const isPastDate = (date) => {
    if (!date) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  const isConflict = (date) => {
    if (!date) return false
    const dateStr = toLocalYmd(date)
    return selectedDates.has(dateStr) && missionDates.has(dateStr)
  }

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate)
    if (viewMode === "month") {
      newDate.setMonth(newDate.getMonth() + direction)
    } else {
      newDate.setDate(newDate.getDate() + direction * 7)
    }
    setCurrentDate(newDate)
  }

  const saveDisponibilites = async () => {
    try {
      setSaving(true)
      setMessage("")

      const availabilities = Array.from(selectedDates).map((date) => ({
        date,
        available: true,
      }))

      await api.saveMyAvailabilities(availabilities)

      setMessage("Disponibilités sauvegardées avec succès !")
      setTimeout(() => setMessage(""), 3000)
    } catch (error) {
      setMessage("Erreur lors de la sauvegarde")
      console.error("Erreur sauvegarde disponibilités:", error)
    } finally {
      setSaving(false)
    }
  }

  // IA pour optimiser les disponibilités
  const getAISuggestion = async () => {
    if (!aiSuggestion.trim()) return

    try {
      setAiLoading(true)
      const response = await api.analyzeSearchQuery(aiSuggestion)

      if (response.success && response.data) {
        const suggestion = response.data
        // Appliquer les suggestions de l'IA
        const suggestedDates = suggestion.recommended_dates || []
        if (suggestedDates.length > 0) {
          const newSelected = new Set(selectedDates)
          suggestedDates.forEach((date) => newSelected.add(date))
          setSelectedDates(newSelected)
          setMessage(`IA a ajouté ${suggestedDates.length} jours recommandés !`)
          setTimeout(() => setMessage(""), 5000)
        }
      }
    } catch (error) {
      console.error("Erreur IA:", error)
    } finally {
      setAiLoading(false)
    }
  }

  const loadAvailabilities = async () => {
    try {
      setLoading(true)
      const month = currentDate.getMonth() + 1
      const year = currentDate.getFullYear()

      const response = await api.getMyAvailabilities(month, year)
      const availabilities = response.data || {}

      const selectedDatesSet = new Set()
      Object.keys(availabilities).forEach((date) => {
        if (availabilities[date]) {
          selectedDatesSet.add(date)
        }
      })

      setSelectedDates(selectedDatesSet)
    } catch (error) {
      console.error("Erreur chargement disponibilités:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadMissionDates = async () => {
    try {
      const month = currentDate.getMonth() + 1
      const year = currentDate.getFullYear()
      const resp = await api.getMyMissionDates(month, year)
      const days = Array.isArray(resp.data) ? resp.data : []
      setMissionDates(new Set(days))
    } catch (e) {
      console.error("Erreur chargement dates missions:", e)
      setMissionDates(new Set())
    }
  }

  useEffect(() => {
    loadAvailabilities()
    loadMissionDates()
  }, [currentDate])

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "s":
            e.preventDefault()
            if (selectedDates.size > 0) saveDisponibilites()
            break
          case "a":
            e.preventDefault()
            setBulkOperation("select")
            break
          case "d":
            e.preventDefault()
            setBulkOperation("deselect")
            break
          case "h":
            e.preventDefault()
            setShowHelp(!showHelp)
            break
        }
      }
    }

    window.addEventListener("keydown", handleKeyPress)
    return () => window.removeEventListener("keydown", handleKeyPress)
  }, [selectedDates.size, showHelp])

  const formatMonthYear = (date) => {
    return date.toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    })
  }

  const formatWeekRange = (date) => {
    const week = generateWeek(date)
    const start = week[0]
    const end = week[6]
    return `${start.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`
  }

  const handleBulkOperation = (operation) => {
    if (operation === "select") {
      const allDates = calendarDays.filter((date) => date && !isPastDate(date)).map((date) => toLocalYmd(date))
      setSelectedDates(new Set(allDates))
    } else if (operation === "deselect") {
      setSelectedDates(new Set())
    }
    setBulkOperation("none")
  }

  const getDateClasses = (date) => {
    if (!date) return "bg-transparent border-transparent cursor-default"
    if (isPastDate(date)) return "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
    if (isConflict(date)) return "bg-red-100 border-red-400 text-red-800 hover:bg-red-200 cursor-pointer"
    if (isMissionDate(date)) return "bg-purple-100 border-purple-400 text-purple-800 hover:bg-purple-200 cursor-pointer"
    if (isToday(date)) {
      return isDateSelected(date)
        ? "bg-emerald-500 border-emerald-500 text-white shadow-lg"
        : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
    }
    return isDateSelected(date)
      ? "bg-emerald-500 border-emerald-500 text-white shadow-lg hover:bg-emerald-600"
      : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-emerald-300"
  }

  const getConflictCount = () => {
    return Array.from(selectedDates).filter((date) => missionDates.has(date)).length
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header avec animations */}
        <div className="text-center animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 bg-clip-text text-transparent animate-pulse">
            Gestion des Disponibilités
          </h1>
          <p className="text-gray-300 mt-3 text-lg">Planifiez vos créneaux et suivez vos missions avec l'IA</p>

          {/* Raccourcis clavier */}
          <div className="mt-4 flex justify-center gap-2 text-xs text-gray-400">
            <span className="px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg shadow-sm">⌘+S: Sauvegarder</span>
            <span className="px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg shadow-sm">
              ⌘+A: Tout sélectionner
            </span>
            <span className="px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg shadow-sm">⌘+H: Aide</span>
          </div>
        </div>

        {/* Stats Cards avec animations */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg p-6 hover:shadow-xl hover:shadow-amber-500/10 transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Jours Disponibles</p>
                <p className="text-2xl font-bold text-white">{selectedDates.size}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg p-6 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Missions Actives</p>
                <p className="text-2xl font-bold text-white">{missionDates.size}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg p-6 hover:shadow-xl hover:shadow-red-500/10 transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Conflits</p>
                <p className="text-2xl font-bold text-white">{getConflictCount()}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg p-6 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Période</p>
                <p className="text-lg font-semibold text-white">
                  {viewMode === "month" ? formatMonthYear(currentDate) : formatWeekRange(currentDate)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* AI Assistant */}
        <div className="bg-gradient-to-r from-gray-800 via-gray-800 to-amber-900/20 rounded-2xl shadow-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Assistant IA</h3>
                <p className="text-sm text-gray-400">Optimisez vos disponibilités avec l'intelligence artificielle</p>
              </div>
            </div>
            <button
              onClick={() => setShowAIModal(!showAIModal)}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg hover:from-amber-600 hover:to-yellow-700 transition-all duration-200"
            >
              {showAIModal ? "Fermer" : "Ouvrir"}
            </button>
          </div>

          {showAIModal && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiSuggestion}
                  onChange={(e) => setAiSuggestion(e.target.value)}
                  placeholder="Ex: 'Je veux être disponible les weekends et éviter les conflits avec mes missions'"
                  className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <button
                  onClick={getAISuggestion}
                  disabled={aiLoading || !aiSuggestion.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg hover:from-amber-600 hover:to-yellow-700 disabled:opacity-50 transition-all duration-200"
                >
                  {aiLoading ? "Analyse..." : "Analyser"}
                </button>
              </div>
              <p className="text-xs text-gray-400">
                💡 L'IA analysera vos besoins et suggérera des optimisations pour vos disponibilités
              </p>
            </div>
          )}
        </div>

        {/* Calendar Container */}
        <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl overflow-hidden">
          {/* Calendar Header */}
          <div className="px-6 py-5 bg-gradient-to-r from-gray-800 to-amber-900/20 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <div className="text-sm text-gray-400">Vue</div>
                  <div className="text-lg font-semibold text-white">
                    {viewMode === "month" ? formatMonthYear(currentDate) : formatWeekRange(currentDate)}
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("month")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      viewMode === "month" ? "bg-amber-500 text-white shadow-sm" : "text-gray-300 hover:text-white"
                    }`}
                  >
                    Mois
                  </button>
                  <button
                    onClick={() => setViewMode("week")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      viewMode === "week" ? "bg-amber-500 text-white shadow-sm" : "text-gray-300 hover:text-white"
                    }`}
                  >
                    Semaine
                  </button>
                </div>

                {/* Filtres */}
                <div className="flex bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setFilterMode("all")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      filterMode === "all" ? "bg-amber-500 text-white shadow-sm" : "text-gray-300 hover:text-white"
                    }`}
                  >
                    Tout
                  </button>
                  <button
                    onClick={() => setFilterMode("available")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      filterMode === "available"
                        ? "bg-amber-500 text-white shadow-sm"
                        : "text-gray-300 hover:text-white"
                    }`}
                  >
                    Disponibles
                  </button>
                  <button
                    onClick={() => setFilterMode("missions")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      filterMode === "missions" ? "bg-amber-500 text-white shadow-sm" : "text-gray-300 hover:text-white"
                    }`}
                  >
                    Missions
                  </button>
                  <button
                    onClick={() => setFilterMode("conflicts")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      filterMode === "conflicts"
                        ? "bg-amber-500 text-white shadow-sm"
                        : "text-gray-300 hover:text-white"
                    }`}
                  >
                    Conflits
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => changeMonth(-1)} className="p-2 rounded-lg hover:bg-gray-700 transition-colors">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 text-sm bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg hover:from-amber-600 hover:to-yellow-700 transition-all duration-200"
                >
                  Aujourd'hui
                </button>
                <button onClick={() => changeMonth(1)} className="p-2 rounded-lg hover:bg-gray-700 transition-colors">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Bulk Operations */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-300">Actions rapides:</span>
              <button
                onClick={() => setBulkOperation("select")}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Sélectionner tout le mois
              </button>
              <button
                onClick={() => setBulkOperation("deselect")}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Effacer tout
              </button>
              {bulkOperation !== "none" && (
                <div className="flex items-center gap-2 animate-fade-in">
                  <span className="text-sm text-gray-400">Confirmer?</span>
                  <button
                    onClick={() => handleBulkOperation(bulkOperation)}
                    className="px-3 py-1 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    Oui
                  </button>
                  <button
                    onClick={() => setBulkOperation("none")}
                    className="px-3 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Non
                  </button>
                </div>
              )}
            </div>

            {/* Calendar Grid */}
            <div className="mb-6">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {filteredDays.map((date, index) => (
                  <button
                    key={index}
                    onClick={() => (isMissionDate(date) ? handleMissionDateClick(date) : toggleDate(date))}
                    disabled={!date || isPastDate(date)}
                    className={`
                      aspect-square rounded-lg border-2 text-xs font-medium transition-all duration-200 transform hover:scale-105
                      ${getDateClasses(date)}
                    `}
                  >
                    {date ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <span className="text-sm">{date.getDate()}</span>
                        {isMissionDate(date) && <div className="w-2 h-2 bg-purple-400 rounded-full mt-1"></div>}
                        {isDateSelected(date) && !isMissionDate(date) && (
                          <div className="w-2 h-2 bg-amber-400 rounded-full mt-1"></div>
                        )}
                        {isConflict(date) && <div className="w-2 h-2 bg-red-400 rounded-full mt-1"></div>}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-300">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-500 rounded border"></div>
                <span>Disponible</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-600 border-2 border-purple-400 rounded"></div>
                <span>Mission (cliquable)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-600 border-2 border-red-400 rounded"></div>
                <span>Conflit</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-600 border-2 border-blue-400 rounded"></div>
                <span>Aujourd'hui</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-600 border-2 border-gray-500 rounded"></div>
                <span>Passé</span>
              </div>
            </div>

            {/* Status Message */}
            {message && (
              <div
                className={`mt-4 p-3 rounded-lg text-sm animate-fade-in ${
                  message.includes("succès")
                    ? "bg-green-900 text-green-300 border border-green-700"
                    : "bg-red-900 text-red-300 border border-red-700"
                }`}
              >
                {message}
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm text-gray-400">
                {selectedDates.size} jour{selectedDates.size > 1 ? "s" : ""} sélectionné
                {selectedDates.size > 1 ? "s" : ""}
                {getConflictCount() > 0 && (
                  <span className="ml-2 text-red-400">
                    ⚠️ {getConflictCount()} conflit{getConflictCount() > 1 ? "s" : ""} détecté
                    {getConflictCount() > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedDates(new Set())}
                  className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Effacer tout
                </button>
                <button
                  onClick={saveDisponibilites}
                  disabled={saving || selectedDates.size === 0}
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-600 text-white hover:from-amber-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {saving ? "Sauvegarde..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Dates Summary */}
        {selectedDates.size > 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl overflow-hidden animate-fade-in">
            <div className="px-6 py-5 bg-gradient-to-r from-gray-800 to-amber-900/20 border-b border-gray-700">
              <div className="text-sm text-gray-400">Résumé</div>
              <div className="text-lg font-semibold text-white">Jours sélectionnés</div>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {Array.from(selectedDates)
                  .sort()
                  .map((date) => (
                    <span
                      key={date}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        missionDates.has(date)
                          ? "bg-red-900 text-red-300 border border-red-700"
                          : "bg-amber-900 text-amber-300 border border-amber-700"
                      }`}
                    >
                      {new Date(date).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "short",
                      })}
                      {missionDates.has(date) && " ⚠️"}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Mission Details Modal */}
        {showMissionModal && missionDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-5 bg-gradient-to-r from-gray-800 to-purple-900/20 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-400">Détails de la mission</div>
                    <div className="text-lg font-semibold text-white">
                      {new Date(selectedMissionDate).toLocaleDateString("fr-FR", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMissionModal(false)}
                    className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {missionDetails.map((mission, index) => (
                  <div key={mission.id} className="border border-gray-700 rounded-lg p-4 bg-gray-750">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-semibold text-white mb-2">Client</h3>
                        <p className="text-gray-300">{mission.client_name}</p>
                        <p className="text-gray-400 text-sm">{mission.client_phone}</p>
                      </div>

                      <div>
                        <h3 className="font-semibold text-white mb-2">Statut</h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            mission.status === "scheduled"
                              ? "bg-blue-900 text-blue-300"
                              : "bg-yellow-900 text-yellow-300"
                          }`}
                        >
                          {mission.status === "scheduled" ? "Programmée" : "En cours"}
                        </span>
                      </div>

                      <div>
                        <h3 className="font-semibold text-white mb-2">Adresses</h3>
                        <p className="text-gray-300 text-sm">
                          <strong>Départ:</strong> {mission.from_address}
                        </p>
                        <p className="text-sm text-gray-300">
                          <strong>Arrivée:</strong> {mission.to_address}
                        </p>
                      </div>

                      <div>
                        <h3 className="font-semibold text-white mb-2">Détails</h3>
                        <p className="text-gray-300 text-sm">
                          <strong>Volume:</strong> {mission.volume_m3} m³
                        </p>
                        <p className="text-gray-300 text-sm">
                          <strong>Camion:</strong> {mission.truck_type}
                        </p>
                        {mission.price_cents && (
                          <p className="text-gray-300 text-sm">
                            <strong>Prix:</strong> {(mission.price_cents / 100).toFixed(2)} TND
                          </p>
                        )}
                      </div>
                    </div>

                    {mission.description && (
                      <div className="mt-4">
                        <h3 className="font-semibold text-white mb-2">Description</h3>
                        <p className="text-gray-300 text-sm">{mission.description}</p>
                      </div>
                    )}

                    {mission.housing_type && (
                      <div className="mt-4">
                        <h3 className="font-semibold text-white mb-2">Type de logement</h3>
                        <p className="text-gray-300 text-sm">
                          {mission.housing_type}
                          {mission.floor && ` - Étage ${mission.floor}`}
                          {mission.has_elevator && " - Avec ascenseur"}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {showHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-5 bg-gradient-to-r from-gray-800 to-blue-900/20 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-400">Aide</div>
                    <div className="text-lg font-semibold text-white">Guide d'utilisation</div>
                  </div>
                  <button
                    onClick={() => setShowHelp(false)}
                    className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4 text-gray-300">
                <div>
                  <h3 className="font-semibold text-white mb-2">🎯 Raccourcis clavier</h3>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>
                      <strong>⌘+S:</strong> Sauvegarder les disponibilités
                    </li>
                    <li>
                      <strong>⌘+A:</strong> Sélectionner tout le mois
                    </li>
                    <li>
                      <strong>⌘+D:</strong> Effacer toutes les sélections
                    </li>
                    <li>
                      <strong>⌘+H:</strong> Afficher cette aide
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">🤖 Assistant IA</h3>
                  <p className="text-sm text-gray-300">
                    Utilisez l'assistant IA pour optimiser vos disponibilités. Décrivez vos besoins et l'IA vous
                    suggérera des améliorations.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">🔍 Filtres</h3>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>
                      <strong>Tout:</strong> Afficher tous les jours
                    </li>
                    <li>
                      <strong>Disponibles:</strong> Seulement les jours sélectionnés
                    </li>
                    <li>
                      <strong>Missions:</strong> Seulement les jours avec missions
                    </li>
                    <li>
                      <strong>Conflits:</strong> Jours avec disponibilités ET missions
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">📅 Vues</h3>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>
                      <strong>Mois:</strong> Vue mensuelle complète
                    </li>
                    <li>
                      <strong>Semaine:</strong> Vue hebdomadaire détaillée
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
